import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { debounce } from 'lodash';
import { z } from 'zod';
import { StarIcon, Trash2Icon, XIcon } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogBody,
    DialogDescription,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { useFetch } from '@/hooks/useFecth';
import useDialog from '@/hooks/useDialog';
import server from '@/services/server';
import useClubErrors from '../hooks/useClubErrors';
import useClubPortalContainer from '../hooks/useClubPortalContainer';
import { useClubConfirm } from '../providers/ClubConfirmProvider';
import ClubSettingsCardHeader from './ClubSettingsCardHeader';

const schema = z.object({
    name: z.string().min(1, 'Informe o nome do destaque'),
    secondaryTitle: z.string().optional(),
    advertiserIds: z.array(z.number()).default([]),
});

function getOfferCount(item) {
    const offers = item?.Offer || item?.offers || [];
    if (!Array.isArray(offers)) return Number(item?.offerCount) || 0;
    return offers.filter(offer => offer.status === true).length;
}

function toSelectedEntry(item) {
    return {
        id: item.id,
        name: item.name,
        offerCount: getOfferCount(item),
    };
}

function DialogFeaturedHighlight({ open, onOpenChange, highlight, mutateHighlight }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const confirm = useClubConfirm();
    const [search, setSearch] = useState('');
    const [input, setInput] = useState('');
    const portalContainer = useClubPortalContainer();
    const [networkOnly, setNetworkOnly] = useState(false);
    const [selectedMap, setSelectedMap] = useState({});

    const setSearchDebounced = useMemo(
        () =>
            debounce(value => {
                setSearch(value);
            }, 300),
        [],
    );

    useEffect(() => () => setSearchDebounced.cancel(), [setSearchDebounced]);

    const handleChange = useCallback(
        value => {
            setInput(value);
            setSearchDebounced(value);
        },
        [setSearchDebounced],
    );

    const handleReset = useCallback(() => {
        setSearchDebounced.cancel();
        setSearch('');
        setInput('');
    }, [setSearchDebounced]);

    const { data: advertisersResponse, isFetching: loadingAdvertisers } = useFetch(
        open ? 'club/advertisers' : null,
        {
            params: {
                page: 1,
                per_page: 50,
                orderBy: 'name_asc',
                // off → rede + catálogo geral; on → só anunciantes do clube (clubId)
                scope: networkOnly ? 'club' : 'all',
                ...(search.trim() ? { search: search.trim() } : {}),
            },
        },
    );

    const advertisers = advertisersResponse?.rows || [];

    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            name: '',
            secondaryTitle: '',
            advertiserIds: [],
        },
    });

    useEffect(() => {
        if (!open) return;
        const initialAdvertisers = highlight?.category?.advertisers || [];
        form.reset({
            name: highlight?.category?.name || highlight?.featured?.primaryTitle || '',
            secondaryTitle: highlight?.featured?.secondaryTitle || '',
            advertiserIds: initialAdvertisers.map(a => a.id),
        });
        setSelectedMap(Object.fromEntries(initialAdvertisers.map(a => [a.id, toSelectedEntry(a)])));
        handleReset();
        setNetworkOnly(false);
    }, [open, highlight, form, handleReset]);

    useEffect(() => {
        if (!advertisers.length) return;
        setSelectedMap(prev => {
            let changed = false;
            const next = { ...prev };
            for (const item of advertisers) {
                if (!next[item.id]) continue;
                const entry = toSelectedEntry(item);
                if (next[item.id].name !== entry.name || next[item.id].offerCount !== entry.offerCount) {
                    next[item.id] = { ...next[item.id], ...entry };
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [advertisers]);

    const selectedIds = form.watch('advertiserIds') || [];

    const selectedAdvertisers = useMemo(
        () => selectedIds.map(id => selectedMap[id]).filter(Boolean),
        [selectedIds, selectedMap],
    );

    const toggleAdvertiser = (item, checked) => {
        const id = item.id;
        const current = form.getValues('advertiserIds') || [];
        if (checked) {
            form.setValue('advertiserIds', [...current, id], { shouldDirty: true });
            setSelectedMap(prev => ({
                ...prev,
                [id]: toSelectedEntry(item),
            }));
            return;
        }
        form.setValue(
            'advertiserIds',
            current.filter(value => value !== id),
            { shouldDirty: true },
        );
        setSelectedMap(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const onSubmit = async values => {
        try {
            await server.put('club/settings/featured', {
                name: values.name,
                secondaryTitle: values.secondaryTitle || null,
                advertiserIds: values.advertiserIds,
            });
            enqueueSnackbar('Destaque da home salvo com sucesso.');
            await mutateHighlight();
            onOpenChange(false);
        } catch (error) {
            setErrors(error);
        }
    };

    const onRemove = async () => {
        try {
            await confirm({
                title: 'Remover destaque',
                description: 'A categoria de destaque será removida da home do clube.',
                confirmationText: 'Remover',
                confirmationButtonProps: { variant: 'destructive' },
            });
            await server.delete('club/settings/featured');
            enqueueSnackbar('Destaque removido.');
            await mutateHighlight();
            onOpenChange(false);
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        }
    };

    const { errors, isSubmitting } = form.formState;
    const hasHighlight = Boolean(highlight?.category?.id);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <ScrollDialogContent container={portalContainer} className="sm:max-w-xl">
                <ScrollDialogHeader>
                    <DialogTitle>Destaque na home</DialogTitle>
                    <DialogDescription>
                        Crie uma bloco de destaque na home e escolha os anunciantes que aparecem.
                    </DialogDescription>
                </ScrollDialogHeader>

                <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
                    <DialogBody>
                        <div className="flex flex-col gap-4">
                            <Alert>
                                <AlertDescription>
                                    Só é permitido 1 destaque. Ele aparece na home do app e do site do clube em até 15
                                    minutos.
                                </AlertDescription>
                            </Alert>

                            <FieldGroup>
                                <Field data-invalid={Boolean(errors.name)}>
                                    <FieldLabel htmlFor="featured-name">Nome do destaque</FieldLabel>
                                    <Input
                                        id="featured-name"
                                        placeholder="Ex.: Parceiros do clube"
                                        aria-invalid={Boolean(errors.name)}
                                        {...form.register('name')}
                                    />
                                    <FieldError>{errors.name?.message}</FieldError>
                                </Field>

                                <Field>
                                    <FieldLabel htmlFor="featured-secondary">Subtítulo (opcional)</FieldLabel>
                                    <Input
                                        id="featured-secondary"
                                        placeholder="Ex.: Ofertas selecionadas para você"
                                        {...form.register('secondaryTitle')}
                                    />
                                </Field>
                            </FieldGroup>

                            <div className="flex flex-col gap-2">
                                <div className="flex items-center justify-between gap-3">
                                    <FieldLabel>Anunciantes ({selectedIds.length} selecionados)</FieldLabel>
                                    <Field orientation="horizontal" className="w-auto gap-2">
                                        <FieldLabel htmlFor="featured-network" className="text-xs font-normal">
                                            Minha rede
                                        </FieldLabel>
                                        <Switch
                                            id="featured-network"
                                            checked={networkOnly}
                                            onCheckedChange={checked => {
                                                setNetworkOnly(Boolean(checked));
                                                handleReset();
                                            }}
                                        />
                                    </Field>
                                </div>
                                <div className="rounded-lg border border-border bg-muted/30 p-2">
                                    {selectedAdvertisers.length === 0 ? (
                                        <p className="px-1 py-2 text-center text-xs text-muted-foreground">
                                            Nenhum anunciante selecionado ainda.
                                        </p>
                                    ) : (
                                        <div className="flex max-h-28 flex-wrap gap-1.5 overflow-y-auto">
                                            {selectedAdvertisers.map(item => (
                                                <Badge key={item.id} variant="secondary" className="gap-1 pr-1">
                                                    <span className="max-w-40 truncate">{item.name}</span>
                                                    <button
                                                        type="button"
                                                        className="rounded-full p-0.5 hover:bg-black/10"
                                                        aria-label={`Remover ${item.name}`}
                                                        onClick={() => toggleAdvertiser(item, false)}
                                                    >
                                                        <XIcon className="size-3" />
                                                    </button>
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <p className="text-xs text-muted-foreground">
                                    {networkOnly
                                        ? 'Só anunciantes da sua rede. Digite o nome para buscar.'
                                        : 'Sua rede e o catálogo geral. Digite o nome para buscar.'}
                                </p>
                                <Input
                                    placeholder="Digite o nome para buscar"
                                    value={input}
                                    onChange={e => handleChange(e.target.value)}
                                />
                                <div className="max-h-64 overflow-y-auto rounded-lg border border-border p-2">
                                    {loadingAdvertisers ? (
                                        <div className="flex justify-center py-6">
                                            <Spinner className="size-5" />
                                        </div>
                                    ) : advertisers.length === 0 ? (
                                        <p className="py-4 text-center text-sm text-muted-foreground">
                                            {search.trim()
                                                ? 'Nenhum anunciante encontrado.'
                                                : networkOnly
                                                  ? 'Nenhum anunciante na sua rede.'
                                                  : 'Nenhum anunciante disponível.'}
                                        </p>
                                    ) : (
                                        <ul className="flex flex-col gap-1">
                                            {advertisers.map(item => {
                                                const checked = selectedIds.includes(item.id);
                                                const offerCount = getOfferCount(item);
                                                return (
                                                    <li key={item.id}>
                                                        <label className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/60">
                                                            <Checkbox
                                                                checked={checked}
                                                                onCheckedChange={value =>
                                                                    toggleAdvertiser(item, Boolean(value))
                                                                }
                                                            />
                                                            <span className="min-w-0 flex-1 truncate text-sm">
                                                                {item.name}
                                                            </span>
                                                            {!networkOnly ? (
                                                                <Badge variant="outline">
                                                                    {item.clubId ? 'Rede' : 'Geral'}
                                                                </Badge>
                                                            ) : null}
                                                            <Badge variant="secondary">
                                                                {offerCount === 1
                                                                    ? '1 oferta'
                                                                    : `${offerCount} ofertas`}
                                                            </Badge>
                                                        </label>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    </DialogBody>

                    <ScrollDialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancelar
                        </Button>
                        {hasHighlight ? (
                            <Button type="button" variant="destructive" disabled={isSubmitting} onClick={onRemove}>
                                {isSubmitting ? <Spinner className="size-4" /> : <Trash2Icon className="size-4" />}
                                Remover
                            </Button>
                        ) : null}
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? <Spinner className="size-4" /> : 'Salvar'}
                        </Button>
                    </ScrollDialogFooter>
                </form>
            </ScrollDialogContent>
        </Dialog>
    );
}

export default function FeaturedHighlightClub() {
    const { open, handleClose } = useDialog('#destaque-home');
    const { data, mutate, isFetching } = useFetch('club/settings/featured');

    const category = data?.category;
    const advertisers = category?.advertisers || [];

    return (
        <>
            <Card>
                <CardHeader>
                    <ClubSettingsCardHeader
                        icon={StarIcon}
                        title="Destaque na home"
                        description="Uma categoria com anunciantes escolhidos para a home do clube."
                    />
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                    {isFetching && !data ? (
                        <Spinner className="size-5" />
                    ) : category ? (
                        <>
                            <div>
                                <span className="text-muted-foreground">Nome</span>
                                <p className="font-medium">{category.name}</p>
                            </div>
                            <div>
                                <span className="text-muted-foreground">Anunciantes</span>
                                <p className="font-medium">
                                    {advertisers.length
                                        ? advertisers.map(a => a.name).join(', ')
                                        : 'Nenhum anunciante selecionado'}
                                </p>
                            </div>
                        </>
                    ) : (
                        <p className="text-muted-foreground">Nenhum destaque configurado.</p>
                    )}
                </CardContent>
                <CardFooter>
                    <Button variant="outline" size="sm" render={<Link to="#destaque-home" />}>
                        <StarIcon className="size-4" />
                        {category ? 'Editar' : 'Configurar destaque'}
                    </Button>
                </CardFooter>
            </Card>

            <DialogFeaturedHighlight
                open={open}
                onOpenChange={isOpen => !isOpen && handleClose()}
                highlight={data}
                mutateHighlight={mutate}
            />
        </>
    );
}
